/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any;
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component);
    if (alias) {
      app.config.globalProperties[alias] = component;
    }
  };
  return component as T & Plugin;
};

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
};

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) {
    return '';
  }
  return str.replace(/-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase();
  });
};

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
};

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val);
};

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop);
};

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn);
  }
  let index = -1;
  ary.some((item: T, i: number, arr: Array<T>) => {
    const ret: T = fn(item, i, arr);
    if (ret) {
      index = i;
      return true; // 显式返回 true 以停止迭代
    }
    return false; // 显式返回 false 以继续迭代
  });
  return index;
};

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '');
};

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) {
    return '';
  } else {
    const date = new Date(time);
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds(),
    };
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length),
        );
      }
    }
    return fmt;
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0;
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString();
  });
  return str;
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase());
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData();
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key]);
  });
  return formData;
}

/**
 * 手机号码正则
 */
export function verifyPhone(phone: string): { valid: boolean; msg: string } {
  if (!phone) {
    return { valid: false, msg: '请输入手机号码' };
  }
  return /^1[3456789]\d{9}$/.test(phone)
    ? { valid: true, msg: '' }
    : { valid: false, msg: '请输入正确的手机号码' };
}

/**
 * 密码校验
 */
export function verifyPassword(value: string): { valid: boolean; msg: string } {
  if (!value) {
    return { valid: false, msg: '请输入密码' };
  }
  if (value.length > 20 || value.length < 8) {
    return { valid: false, msg: '密码长度需要8-20位' };
  }
  const hasLetter = /[a-zA-Z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSymbol = /[~`!@#$%^&*()_+\-={}:;"'<,>.?/]/g.test(value); // 符号且非空格

  // 检查是否至少包含两种类型
  let count = 0;
  if (hasLetter) {
    count++;
  }
  if (hasNumber) {
    count++;
  }
  if (hasSymbol) {
    count++;
  }
  if (count >= 2) {
    return { valid: true, msg: '' };
  }
  return { valid: false, msg: '密码至少包含字母、数字和符号中两种' };
}

// 防抖
export function debounce(func: Function, wait = 200) {
  let timeout;
  return function (this, ...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}
